import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  DestroyRef,
  ViewChild,
  ElementRef,
  NgZone,
  ChangeDetectorRef,
  TrackByFunction,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of, take } from 'rxjs';
import { map, filter, switchMap } from 'rxjs/operators';
import { SessionStorageService } from '@app/core/auth/session-storage';
import {
  ReviewService,
  HospitalIdentity,
} from '@app/features/admin/reviews/reviews';
import {
  Review,
  DisplayReview,
  ReviewStatus,
} from '@app/shared/models/review.model';
import { BreadcrumbComponent } from '@app/shared/components/breadcrumb/breadcrumb';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner';
import { FetchState, PatchState } from '@app/shared/models/fetch-state.model';
import { EllipsisDirective } from '@app/shared/directives/ellipsis';
import { formatId } from '@app/shared/utils/id-formats/id-formats';
import { YyyyMMddFormatPipe } from '@app/shared/pipes/yyyyMMddFormat/yyyyMMddFormat';
import { idFormatPipe } from '@app/shared/pipes/idFormat/idFormat';

@Component({
  selector: 'app-admin-reviews',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    BreadcrumbComponent,
    SpinnerComponent,
    SkeletonComponent,
    YyyyMMddFormatPipe,
    idFormatPipe,
    EllipsisDirective,
  ],
  templateUrl: './reviews.html',
  providers: [DatePipe],
  styleUrls: ['./reviews.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewsComponent implements OnInit {
  reviewState$: Observable<FetchState<Review[]>> = of({ status: 'loading' });
  patchState$: Observable<PatchState> = of({ status: 'idle' });

  selectedItemId: string | null = null;
  readonly dataSource = new MatTableDataSource<DisplayReview>([]);
  readonly displayedColumns = [
    'id',
    'created',
    'hospitalName',
    'status',
    'reviewResult',
    'approverName',
    'reviewed',
    'comment',
    'updated',
    'updaterName',
  ] as const;

  readonly initialReviewStatus = new Map<string, ReviewStatus>();

  isBusy = false;

  private _sort?: MatSort;
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly trackById: TrackByFunction<DisplayReview> = (_i, row) => row.id;

  @ViewChild('tableContainer') tableContainerRef?: ElementRef<HTMLDivElement>;

  @ViewChild(MatSort)
  set matSort(sort: MatSort | undefined) {
    if (!sort) return;
    this._sort = sort;
    this.dataSource.sort = this._sort;
  }

  constructor(
    private readonly reviewService: ReviewService,
    private readonly sessionService: SessionStorageService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    if (this.reviewService.isInitialLoad()) {
      this.reviewService.fetchReviews();
    }
    this.reviewState$ = this.reviewService.reviewState$;
    this.patchState$ = this.reviewService.patchState$;
    this.bindReviewState();
    this.sessionService.removeAdminSelectedReviewId();

    this.reviewService.patchState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((s) => {
        if (s.status !== 'error') return;

        const failedIds = this.isFailedIdsPayload(s.error?.error)
          ? s.error!.error.failedIds
          : [];

        if (failedIds.length > 0) {
          this.showErrorToast(failedIds);
        }
      });
  }

  private bindReviewState(): void {
    this.reviewState$ = this.reviewService.reviewState$;
    this.reviewService.reviewState$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (state): state is FetchState<Review[]> & { status: 'success' } =>
            state.status === 'success',
        ),
        switchMap((state) => {
          const reviews = state.data ?? [];
          if (reviews.length === 0) {
            this.dataSource.data = [];
            this.initialReviewStatus.clear();
            this.isBusy = false;
            this.cdr.markForCheck();
            return of<{
              reviews: Review[];
              dict: Record<string, HospitalIdentity>;
            }>({ reviews: [], dict: {} });
          }

          this.isBusy = true;
          const uniqueCodes = this.uniqueStrings(
            reviews.map((r) => r.hospitalCode),
          );
          return this.reviewService
            .resolveHospitalIdentities(uniqueCodes)
            .pipe(map((dict) => ({ reviews, dict })));
        }),
      )
      .subscribe(({ reviews, dict }) => {
        const baselineRows: DisplayReview[] = reviews.map((r) => {
          const entry = dict[r.hospitalCode];
          return {
            id: r.id,
            created: r.created,
            hospitalCode: r.hospitalCode,
            hospitalName: entry?.alias ?? entry?.name ?? r.hospitalCode,
            reviewResult: r.reviewResult,
            status: r.status,
            approverName: r.approver?.name,
            reviewed: r.reviewed,
            comment: r.comment,
            updated: r.updated,
            updaterName: r.updater?.name,
          };
        });

        this.initialReviewStatus.clear();
        baselineRows.forEach((r) =>
          this.initialReviewStatus.set(r.id, r.status as ReviewStatus),
        );

        // 編集中データを画面上のデータに適用する。病院用の画面に遷移して戻ってきた際に必要な処理
        const draftMap = this.reviewService.getDrafts();
        const rows = baselineRows.map((r) => {
          const draft = draftMap.get(r.id);
          return draft ? { ...r, status: draft.status } : r;
        });

        //デフォルトの並び替えは作成日の降順
        rows.sort((a, b) => b.created.localeCompare(a.created));
        this.dataSource.data = rows;

        if (this._sort) this.dataSource.sort = this._sort;

        this.isBusy = false;
        this.cdr.markForCheck();

        this.restoreSelection(rows);
      });
  }

  onSelect(row: DisplayReview): void {
    this.selectedItemId = row.id;
  }

  goToHospitalReviews(row: DisplayReview): void {
    this.sessionService.saveAdminSelectedReviewId(row.id);
    this.sessionService.saveTargetHospitalCode(row.hospitalCode);
    this.sessionService.saveTargetHospitalName(row.hospitalName);
    this.sessionService.saveFromAdmin('1');
    this.router.navigate(['hospital', 'reviews']);
  }

  onStatusChange(id: string, value: string): void {
    if (!this.isReviewStatus(value)) return;

    const initStatus = this.initialReviewStatus.get(id);
    if (value === initStatus) {
      this.reviewService.removeDraft(id);
    } else {
      this.reviewService.saveDraft(id, { status: value });
    }

    this.dataSource.data = this.dataSource.data.map((r) =>
      r.id === id ? { ...r, status: value } : r,
    );
    this.cdr.markForCheck();
  }

  saveEdit(): void {
    const draftMap = this.reviewService.getDrafts();
    const ids = Array.from(draftMap.keys());
    if (ids.length === 0) return;

    const lines = ids.map((id) => {
      const next = draftMap.get(id)!.status;
      const prev = this.initialReviewStatus.get(id);
      return `・${formatId(id)}（${this.statusLabel(prev)} → ${this.statusLabel(next)}）`;
    });

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        iconName: 'alert',
        message: ['以下のレビュー状況を更新してもよろしいですか？', ...lines],
        primaryText: '登録',
        secondaryText: 'キャンセル',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ok) => {
        if (!ok) return;
        const requests = ids.map((id) => ({
          resourceType: 'Review' as const,
          id,
          status: draftMap.get(id)!.status,
          updater: {
            id: this.sessionService.getUserId(),
            name: this.sessionService.getUserName(),
          },
        }));
        this.reviewService.patchReviewsBatch(requests);
      });
  }

  private statusLabel(val: unknown): string {
    const map: Record<ReviewStatus, string> = {
      preparation: '準備中',
      processing: 'レビュー中',
      finished: '完了',
    };
    return map[val as ReviewStatus] ?? '';
  }

  mapReviewResult(val: unknown): string {
    if (val == null) return '未承認';
    const map: Record<string, string> = { approved: '承認', rejected: '却下' };
    return map[val as string] ?? '';
  }

  private isReviewStatus(v: string): v is ReviewStatus {
    return v === 'preparation' || v === 'processing' || v === 'finished';
  }

  private isFailedIdsPayload(x: unknown): x is { failedIds: string[] } {
    if (!x || typeof x !== 'object') return false;
    const arr = (x as { failedIds?: unknown }).failedIds;
    return Array.isArray(arr) && arr.every((id) => typeof id === 'string');
  }

  private showErrorToast(failedIds: string[]): void {
    this.snackBar.dismiss();
    const message = [
      '下記管理番号のデータ更新に失敗しました。',
      ...failedIds.map((id) => `・${formatId(id)}`),
    ].join('\n');

    const ref = this.snackBar.open(message, '×', {
      panelClass: 'error-toast',
      verticalPosition: 'top',
      horizontalPosition: 'center',
    });
    ref
      .onAction()
      .pipe(take(1))
      .subscribe(() => ref.dismiss());
  }

  private restoreSelection(rows: DisplayReview[]): void {
    const storedId = this.sessionService.getAdminSelectedReviewId();
    const target =
      (storedId && rows.find((r) => r.id === storedId)) ||
      (this.selectedItemId && rows.find((r) => r.id === this.selectedItemId)) ||
      rows[0];

    if (!target) return;

    this.onSelect(target);
    this.ngZone.onStable
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.scrollToRowById(target.id));
  }

  scrollToRowById(id: string): void {
    const container = this.tableContainerRef?.nativeElement;
    if (!container) return;
    const rowEl = container.querySelector<HTMLElement>(
      `tr[data-row-id="${id}"]`,
    );
    rowEl?.scrollIntoView({ block: 'nearest' });
  }

  private uniqueStrings(values: (string | null | undefined)[]): string[] {
    return Array.from(
      new Set(
        (values ?? []).filter(
          (v): v is string => typeof v === 'string' && v.trim().length > 0,
        ),
      ),
    );
  }
}
